import { useState, useEffect } from 'react'
import TopSafeSpacer from '../layout/TopSafeSpacer'
import { useParams, useNavigate } from 'react-router-dom'
import { logger } from '../../utils/logger'
import { CURRENCIES } from '../../utils/constants'
import { productApi, CategoryNode } from '../../services/api'
import MultiImageUploader from '../product/MultiImageUploader'
import VideoUploader from '../product/VideoUploader'
import TagSelector from '../ui/TagSelector'
import CategoryAttributePicker, { findCategoryPath } from '../ui/CategoryAttributePicker'
import LocationSelector from '../ui/LocationSelector'
import MapPointPicker from '../ui/MapPointPicker'
import { ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react'
import { t } from '../../i18n/translations'

export default function ProductEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null

  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState<{
    name: string
    description: string
    price: string
    currency: string
    quantity: string
    images?: any[]
    videos?: any[]
  }>({
    name: '',
    description: '',
    price: '',
    currency: 'USD',
    quantity: '1',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  const [categories, setCategories] = useState<CategoryNode[]>([])
  const [selectedPath, setSelectedPath] = useState<CategoryNode[]>([])
  const [productAttributes, setProductAttributes] = useState<Record<string, any>>({})
  // Set once the product is loaded; used to seed the picker after the tree arrives.
  const [loadedCategoryId, setLoadedCategoryId] = useState<string | null>(null)

  const [productLocation, setProductLocation] = useState<{
    country_code: string | null
    city_name: string | null
  }>({ country_code: null, city_name: null })

  const [userHomeLocation, setUserHomeLocation] = useState<{
    country_code: string | null
    city_name: string | null
  } | null>(null)

  // Precise map point (optional). Prefilled from the loaded product when present.
  const [showMapPicker, setShowMapPicker] = useState(false)
  const [mapPoint, setMapPoint] = useState<{
    lat: number
    lng: number
    radius: number
  } | null>(null)
  // Tracks whether the loaded product already had a precise point, so we can tell
  // the backend to clear it (via the (0,0) sentinel) if the user removes the pin.
  const [hadInitialPoint, setHadInitialPoint] = useState(false)

  // Fetch user's home location for "Same as home region" button
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const location = await productApi.getUserLocation()
        if (location.home_country_code || location.home_city_name) {
          setUserHomeLocation({
            country_code: location.home_country_code,
            city_name: location.home_city_name
          })
        }
      } catch (error) {
        logger.warn('ProductEdit: Failed to fetch user location:', error)
      }
    }

    fetchUserLocation()
  }, [])

  // Load the category tree (with attributes) for the picker
  useEffect(() => {
    const fetchTree = async () => {
      try {
        const data = await productApi.getCategoryTree('en')
        setCategories(data.categories || [])
      } catch (error) {
        logger.warn('ProductEdit: Failed to load category tree:', error)
      }
    }
    fetchTree()
  }, [])

  // Once both the tree and the product's category id are available, seed the picker path.
  useEffect(() => {
    if (!loadedCategoryId || categories.length === 0) return
    const path = findCategoryPath(categories, loadedCategoryId)
    if (path) setSelectedPath(path)
  }, [loadedCategoryId, categories])

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) {
        navigate('/profile')
        return
      }

      try {
        logger.debug('ProductEdit: Fetching product for edit', id)
        // Use admin endpoint to get product with tags
        const product = await productApi.getProductForEdit(id)
        logger.debug('ProductEdit: Product fetched with tags', product)

        setFormData({
          name: product.name,
          description: product.description || '',
          price: product.price.toString(),
          currency: product.currency || 'USD',
          quantity: product.quantity?.toString() || '1',
          images: product.images || [],  // Load all images from the product
          videos: product.videos || []   // Load all videos from the product
        })

        // Load product location
        setProductLocation({
          country_code: product.country_code || null,
          city_name: product.city_name || null
        })

        // Prefill precise map point if the product has one; auto-expand the picker.
        if (
          product.location_lat != null &&
          product.location_lng != null &&
          !(product.location_lat === 0 && product.location_lng === 0)
        ) {
          setMapPoint({
            lat: product.location_lat,
            lng: product.location_lng,
            radius: product.location_radius_km ?? 5
          })
          setHadInitialPoint(true)
          setShowMapPicker(true)
        }

        // Seed category + attribute values (picker path is set once the tree loads)
        if (product.category_id) {
          setLoadedCategoryId(product.category_id)
        }
        if (product.attributes && typeof product.attributes === 'object') {
          setProductAttributes(product.attributes)
        }

        // Load product tags if they exist (backend returns them as tag_list)
        if (product.tag_list && Array.isArray(product.tag_list) && product.tag_list.length > 0) {
          const tagIds = product.tag_list.map((tag) => tag.id)
          logger.debug('ProductEdit: Loading existing tags', { tag_list: product.tag_list, tagIds })
          setSelectedTagIds(tagIds)
        } else {
          logger.debug('ProductEdit: No tags found for product', { tag_list: product.tag_list })
        }
      } catch (error) {
        logger.error('ProductEdit: Error fetching product:', error)
        navigate('/profile')
      } finally {
        setLoading(false)
      }
    }

    fetchProduct()
  }, [id, navigate])

  // Removed Telegram BackButton - using custom top bar instead

  // Handler for "Same as home region" button
  const handleSameAsHomeRegion = () => {
    if (userHomeLocation) {
      setProductLocation({
        country_code: userHomeLocation.country_code,
        city_name: userHomeLocation.city_name
      })
      logger.info('ProductEdit: Set location to home region')
    }
  }

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required'
    }

    const price = parseFloat(formData.price)
    if (!formData.price || price <= 0) {
      newErrors.price = 'Price must be greater than 0'
    }

    const quantity = parseInt(formData.quantity)
    if (!formData.quantity || quantity < 0) {
      newErrors.quantity = 'Quantity must be 0 or greater'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    logger.debug('ProductEdit: Submit clicked')

    if (!validate()) {
      logger.warn('ProductEdit: Validation failed', errors)
      return
    }

    if (!id) {
      setErrors({ submit: 'Product ID is missing' })
      return
    }

    setIsSubmitting(true)

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        price: parseFloat(formData.price),
        currency: formData.currency,
        quantity: parseInt(formData.quantity),
        tag_ids: selectedTagIds,  // Always send the current tag selection, even if empty
        category_id: selectedPath.length > 0 ? selectedPath[selectedPath.length - 1].id : null,
        attributes: productAttributes,
        country_code: productLocation.country_code,
        city_name: productLocation.city_name,
        // Precise point: send the current pin, or the (0,0) sentinel to clear an
        // existing one (the backend treats a (0,0) lat/lng pair as "clear point").
        ...(mapPoint
          ? {
              location_lat: mapPoint.lat,
              location_lng: mapPoint.lng,
              location_radius_km: mapPoint.radius,
            }
          : hadInitialPoint
          ? { location_lat: 0, location_lng: 0 }
          : {}),
      }

      logger.debug('ProductEdit: Updating product with payload', payload)

      const result = await productApi.updateProduct(id, payload)
      logger.info('ProductEdit: Product updated successfully', result)

      // Success - navigate back to profile
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('success')
      }

      logger.logNavigation(`/product/${id}/edit`, '/profile')
      navigate('/profile')
    } catch (error) {
      logger.error('ProductEdit: Failed to update product:', error)
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to update product' })

      if (tg?.HapticFeedback) {
        tg.HapticFeedback.notificationOccurred('error')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Top padding for Telegram UI */}
        <TopSafeSpacer />

        {/* Custom Top Bar */}
        <div className="bg-white border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-lg font-semibold text-gray-800">Edit Product</h1>
            <div className="w-16" /> {/* Spacer for centering */}
          </div>
        </div>

        <main className="flex-1 overflow-auto p-4">
          <div className="max-w-md mx-auto text-center py-8">
            <div className="loading-shimmer w-12 h-12 rounded-full mx-auto mb-2"></div>
            <p className="text-gray-500 text-sm">Loading product...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Top padding for Telegram UI */}
      <TopSafeSpacer />

      {/* Custom Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">Back</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-800">Edit Product</h1>
          <div className="w-16" /> {/* Spacer for centering */}
        </div>
      </div>

      <main className="flex-1 overflow-auto p-4">
        <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
          {/* Product Name */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Product Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              } bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter product name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe your product (optional)"
              rows={4}
            />
          </div>

          {/* Price and Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Price *
              </label>
              <input
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                className={`w-full px-4 py-3 rounded-lg border ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                } bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                placeholder="0.00"
              />
              {errors.price && (
                <p className="mt-1 text-sm text-red-500">{errors.price}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-tg-text mb-1">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} {currency.symbol}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Quantity *
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.quantity ? 'border-red-500' : 'border-gray-300'
              } bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="1"
              min="0"
            />
            {errors.quantity && (
              <p className="mt-1 text-sm text-red-500">{errors.quantity}</p>
            )}
          </div>

          {/* Category + specifications */}
          <CategoryAttributePicker
            categories={categories}
            selectedPath={selectedPath}
            onSelectCategory={setSelectedPath}
            attributes={productAttributes}
            onAttributesChange={setProductAttributes}
            label="Category"
          />

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Tags
            </label>
            <TagSelector
              selectedTagIds={selectedTagIds}
              onChange={setSelectedTagIds}
              placeholder="Select tags for your product..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Add tags to help buyers find your product
            </p>
          </div>

          {/* Product Location */}
          <div>
            <label className="block text-sm font-medium text-tg-text mb-1">
              Product Location
            </label>
            <LocationSelector
              countryCode={productLocation.country_code}
              cityName={productLocation.city_name}
              onChange={(country, city) => {
                setProductLocation({
                  country_code: country,
                  city_name: city
                })
              }}
            />
            {userHomeLocation && userHomeLocation.city_name && (
              <button
                type="button"
                onClick={handleSameAsHomeRegion}
                className="mt-2 text-sm text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1"
              >
                <span>📍</span>
                <span>Same as home region ({userHomeLocation.city_name})</span>
              </button>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Where is this product located?
            </p>
          </div>

          {/* Precise Location (optional, collapsible) */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <button
              type="button"
              onClick={() => setShowMapPicker((prev) => !prev)}
              className="w-full flex items-center justify-between text-left"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <span>📍</span>
                <span>{t('location.preciseLocation')}</span>
              </span>
              {showMapPicker ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            <p className="mt-1 text-xs text-gray-500">
              {t('location.preciseHint')}
            </p>
            {showMapPicker && (
              <div className="mt-3">
                <MapPointPicker
                  lat={mapPoint?.lat ?? null}
                  lng={mapPoint?.lng ?? null}
                  radiusKm={mapPoint?.radius ?? null}
                  onChange={(lat, lng, radius) =>
                    setMapPoint({ lat, lng, radius })
                  }
                />
                {mapPoint && (
                  <button
                    type="button"
                    onClick={() => setMapPoint(null)}
                    className="mt-2 text-sm text-red-600 hover:text-red-700 transition-colors"
                  >
                    {t('location.clearPoint')}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Product Images */}
          {id && (
            <MultiImageUploader
              productId={id}
              existingImages={formData.images || []}
              onImagesChange={(images) => {
                setFormData(prev => ({ ...prev, images }))
              }}
            />
          )}

          {/* Product Videos */}
          {id && (
            <VideoUploader
              productId={id}
              existingVideos={formData.videos || []}
              onVideosChange={(videos) => {
                setFormData(prev => ({ ...prev, videos }))
              }}
            />
          )}

          {/* Submit Error */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-3 rounded-lg font-medium transition-colors ${
              isSubmitting
                ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
                : 'bg-blue-500 text-white active:bg-blue-600'
            }`}
          >
            {isSubmitting ? 'Updating Product...' : 'Update Product'}
          </button>
        </form>
      </main>

      {/* Bottom Safe Area */}
      <div className="safe-bottom"></div>
    </div>
  )
}
